/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { AnimateSharedLayout, motion } from "framer-motion";
import { default as React, useEffect, useState } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { GetExternalUsers } from "../api/ExternalUser";
import { SettingsHelpVideosAtom } from "../atoms/SettingsHelpVideos";
import { UserAtom } from "../atoms/UserAtom";
import Header from "../components/ExternalUsers/Header";
import TableRows from "../components/ExternalUsers/TableRows";
import { TableFooter } from "../components/StyledTable";
import useRowsPerPage, { ITableName } from "../hook/useRowsPerPage";
import { notSelect } from "../styles/general";
import { IExternalUser } from "../types/ExternalUserTypes";
import { HelpVideoNameMap, IHelpContexts } from "../types/HelpVideosTypes";

const Body = styled.div`
  min-width: calc(100% + 48px);
  height: 100%;

  display: flex;
  flex-direction: column;

  background-color: #f9f9f9;

  @media screen and (max-width: 1140px) {
    min-width: calc(100% + 32px);
  }
`;

const TableContainer = styled.div<{ isFilterOpen?: boolean }>`
  width: 100%;
  height: ${(props) =>
    !props.isFilterOpen ? "calc(100vh - 145px)" : "calc(100vh - 145px - 72px)"};

  padding: 20px;

  display: flex;
  flex-direction: column;

  overflow-y: scroll;
`;

const TableCard = styled.div`
  height: 60px;

  padding: 15px 37px 15px 14px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border: 1px solid #eaeaeabf;
  background-color: #ffffff;
`;

const TableHead = styled.div`
  display: flex;
  align-items: center;

  font-size: 14px;
  font-weight: 600;
  color: #707070;

  cursor: default;

  position: relative;

  ${notSelect}
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  font-size: 14px;
  font-weight: 600;
  color: #707070;

  cursor: default;
`;

const Icon = styled(motion.img)`
  width: 10px;
  height: 10px;

  margin-left: 8px;

  ${notSelect}

  cursor: pointer;
`;

const FlexRow = styled(motion.div)`
  display: flex;
  align-items: center;

  cursor: pointer;

  :hover {
    opacity: 0.6;
  }
`;

export const FilterUserAtom = atom<string>({
  key: "FilterUserAtom",
  default: "",
});

export default function ExternalUsersContact(): JSX.Element {
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const user = useRecoilValue(UserAtom);
  const [showCard, setShowCard] = useState<string>("");
  const [externalUsers, setExternalUsers] = useState<IExternalUser[]>([]);
  const filterUser = useRecoilValue(FilterUserAtom);
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.USERS
  );

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsUsers],
      helpContext: IHelpContexts.SettingsUsers,
    });
  }, []);

  useEffect(() => {
    getUsers();
  }, [user]);

  async function getUsers() {
    if (!user) return;
    try {
      const newExternalUsers = await GetExternalUsers(user.userUid);

      setExternalUsers(newExternalUsers);
    } catch (err) {
      console.log(err);
    }
  }

  function handleShowCard(vle: string) {
    if (showCard.localeCompare(vle) === 0) setShowCard("");
    else setShowCard(vle);
  }

  return (
    <>
      {/* <SeoHead pageName="Admins and External Users" /> */}
      {/* <TopBar
        title="Admins and External Users"
        subTitle={`${externalUsers.length} users${
          externalUsers.length > 1 ? "s" : ""
        } in total`}
      /> */}
      <Body>
        <Header
          externalUsersCount={externalUsers?.length || 999}
          updateUsers={getUsers}
        />
        <TableContainer>
          <TableCard>
            <TableHead>
              <FlexRow>
                NAME
                <Icon
                  src={"./icons/contacts/arrow.svg"}
                  whileTap={{ scale: 0.8 }}
                />
              </FlexRow>
            </TableHead>
            <TableHead>
              <FlexRow>PASSCODE</FlexRow>
            </TableHead>
            <TableActions>ACTIONS</TableActions>
          </TableCard>
          <AnimateSharedLayout>
            {externalUsers &&
              externalUsers
                ?.filter((c) =>
                  c.name
                    .trim()
                    .toLowerCase()
                    .includes(filterUser.toLowerCase().trim())
                )
                .map((row) => {
                  return (
                    <TableRows
                      updateUsers={getUsers}
                      row={row}
                      isShowing={showCard === row._id}
                      handleShowCard={handleShowCard}
                    />
                  );
                })}
          </AnimateSharedLayout>
          <TableFooter
            page={page}
            length={externalUsers?.length || 0}
            rowsPerPage={cachedRowsPerPage || rowsPerPage}
            setRowsPerPage={(vle: number) => {
              setRowsPerPage(vle);
              updateCachedRowsPerPage(vle);
            }}
            // TODO HANDLE PAGE
            handlePage={() => {}}
          />
        </TableContainer>
      </Body>
    </>
  );
}
